import type { Intersection2d, SimpleMesh2d } from "."
import { getFirstIntersection } from "./getFirstIntersection"
import type { NameVirtualNode } from "./nameVirtualNode"
import { nameVirtualNode } from "./nameVirtualNode"
import { nameShape } from "../nameShape"

type DivideMeshStepResult = {
  result: Readonly<SimpleMesh2d>
  procedure: { intersection: Intersection2d }
} | null

// dividing a mesh means turning all intersections A_B C_D into A_X B_X C_X D_X
export const divideMesh_STEP = (
  mesh: Readonly<SimpleMesh2d>,
  makeNodeId: NameVirtualNode = nameVirtualNode,
): DivideMeshStepResult => {
  const nodes = { ...mesh.nodes }
  const edges = { ...mesh.edges }
  const intersection = getFirstIntersection(mesh)

  if (intersection) {
    const newId = makeNodeId(intersection.node, Object.values(nodes).length)
    const {
      edgeA: [idAB, edgeAB],
      edgeB: [idCD, edgeCD],
    } = intersection
    const newEdges = [edgeAB, edgeCD]
      .map((removedEdge) => [
        {
          node1id: removedEdge.node1id,
          node2id: newId,
        },
        {
          node1id: newId,
          node2id: removedEdge.node2id,
        },
      ])
      .flat()

    delete edges[idAB]
    delete edges[idCD]
    nodes[newId] = intersection.node
    newEdges.forEach((newEdge) => {
      const { node1id, node2id } = newEdge
      const newEdgeId = nameShape(node1id, node2id)
      edges[newEdgeId] = newEdge
    })

    return { result: { nodes, edges }, procedure: { intersection } }
  }

  return null
}

export const divideMesh = (
  mesh: Readonly<SimpleMesh2d>,
  makeNodeId: NameVirtualNode = nameVirtualNode,
): {
  result: Readonly<SimpleMesh2d>
  procedure: { intersections: Intersection2d[] }
} => {
  let result = mesh
  let next: DivideMeshStepResult = null
  let stepNumber = 0
  const procedure: { intersections: Intersection2d[] } = { intersections: [] }
  while ((next || stepNumber === 0) && stepNumber < 100) {
    stepNumber++
    next = divideMesh_STEP(result, makeNodeId)
    if (next) {
      result = next.result
      procedure.intersections.push(next.procedure.intersection)
    }
  }
  if (stepNumber === 100) {
    throw new Error(`divideMesh: cycle limit reached`)
  }

  return { result, procedure }
}
